// Same-origin client for the Pages Functions BFF. The browser NEVER holds the
// first-party API key or any secret; the BFF injects the first-party key and
// forwards the order-access token to /api. The Stripe secret never leaves /api.
import type { PublicPrivacyNoticeVersion } from "@tinkr/shared/privacy-notice";

export class CheckoutApiError extends Error {
  status: number;
  code: string;
  /** `details.reason` from the error envelope, e.g. `provider_country_closed`. */
  reason: string | null;
  /** The envelope's `details` object, untrusted until a caller parses it. */
  details: Record<string, unknown> | null;

  constructor(
    status: number,
    code: string,
    message: string,
    reason: string | null = null,
    details: Record<string, unknown> | null = null
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.reason = reason;
    this.details = details;
  }
}

/** 409 from summary / checkout-session while a multicolor Doodles price is still being calculated (ADR-DSP1). */
export const DOODLES_PRICE_PENDING = "doodles_price_pending";

export function isDoodlesPricePending(error: unknown): error is CheckoutApiError {
  return error instanceof CheckoutApiError && error.code === DOODLES_PRICE_PENDING;
}

// Build-time stub: lets the preview/e2e exercise the screens with no backend.
// Only ever set at build time; never on a deployed environment.
const E2E_STUB = import.meta.env.VITE_E2E_API_STUB === "true";
// The BFF times out upstream requests after 30s. Bound the complete browser
// exchange too, including a response whose headers arrive but body stalls.
const REQUEST_TIMEOUT_MS = 40_000;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  if (E2E_STUB) {
    return stubRequest<T>(path, init?.method ?? "GET", typeof init?.body === "string" ? init.body : undefined);
  }
  const callerSignal = init?.signal;
  if (callerSignal?.aborted) throw callerSignal.reason ?? new DOMException("Request cancelled", "AbortError");
  const controller = new AbortController();
  let rejectInterrupted!: (reason: unknown) => void;
  const interrupted = new Promise<never>((_resolve, reject) => { rejectInterrupted = reject; });
  const abort = (reason: unknown) => {
    rejectInterrupted(reason);
    controller.abort(reason);
  };
  const onCallerAbort = () => abort(callerSignal?.reason ?? new DOMException("Request cancelled", "AbortError"));
  callerSignal?.addEventListener("abort", onCallerAbort, { once: true });
  const timer = setTimeout(() => abort(new CheckoutApiError(504, "request_timeout", "Request timed out. Please try again.")), REQUEST_TIMEOUT_MS);
  try {
    const exchange = async (): Promise<T> => {
      const response = await fetch(`/api/${path}`, {
        ...init,
        signal: controller.signal,
        headers: { "content-type": "application/json", ...init?.headers }
      });
      let payload: unknown = null;
      try {
        payload = await response.json();
      } catch {
        if (controller.signal.aborted) throw controller.signal.reason;
        if (response.ok) throw new CheckoutApiError(502, "invalid_response", "Unable to read the response. Please try again.");
      }
      if (!response.ok) {
        const envelope = payload as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null;
        const details = envelope?.error?.details;
        const reason = details?.reason;
        throw new CheckoutApiError(
          response.status,
          envelope?.error?.code ?? "unknown_error",
          envelope?.error?.message ?? `Request failed (${response.status})`,
          typeof reason === "string" ? reason : null,
          details && typeof details === "object" && !Array.isArray(details) ? details : null
        );
      }
      if (payload === null) throw new CheckoutApiError(502, "invalid_response", "Unable to read the response. Please try again.");
      return payload as T;
    };
    // Also settle adapters which fail to honor fetch's AbortSignal.
    return await Promise.race([exchange(), interrupted]);
  } finally {
    clearTimeout(timer);
    callerSignal?.removeEventListener("abort", onCallerAbort);
  }
}

// --- Types (customer-safe shapes only) ----------------------------------------

export interface JpShippingAddress {
  countryCode: "JP";
  postalCode: string;
  prefecture: string;
  city: string;
  town: string;
  building: string;
  recipientName: string;
  recipientNameKana: string;
  phone: string;
}

// Western/structured shape for non-JP enabled markets (MY is the first), mirrors
// the /api ShippingAddressSummary union (#836).
export interface WesternShippingAddress {
  countryCode: string;
  recipientName: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
}

export type ShippingAddress = JpShippingAddress | WesternShippingAddress;

/**
 * Doodles figurine pricing (ADR-DSP1). Solid figurines and flat-mode countries
 * report `mode: "flat"`; multicolor in a slice-mode country is priced from the
 * figurine's own verified slice. All amounts are minor units in `currency`.
 */
export interface DoodlesPricing {
  mode: "flat" | "slice";
  status: "final" | "pending_estimate" | "fallback_cap";
  currency: string;
  sizeBaseMinor: number;
  multicolorSurchargeMinor: number | null;
  multicolorFloorMinor: number;
  multicolorCapMinor: number | null;
  totalMinor: number;
  estimate: { filamentG: number; printSeconds: number } | null;
}

const PRICING_MODES = new Set(["flat", "slice"]);
const PRICING_STATUSES = new Set(["final", "pending_estimate", "fallback_cap"]);
const isMinor = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

/** Defensive parse of a wire `pricing` object (e.g. from a 409 envelope's details); null when malformed. */
export function parseDoodlesPricing(value: unknown): DoodlesPricing | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (typeof v.mode !== "string" || !PRICING_MODES.has(v.mode)) return null;
  if (typeof v.status !== "string" || !PRICING_STATUSES.has(v.status)) return null;
  if (typeof v.currency !== "string" || !/^[A-Z]{3,4}$/.test(v.currency)) return null;
  if (!isMinor(v.sizeBaseMinor) || !isMinor(v.multicolorFloorMinor) || !isMinor(v.totalMinor)) return null;
  if (v.multicolorSurchargeMinor !== null && !isMinor(v.multicolorSurchargeMinor)) return null;
  if (v.multicolorCapMinor !== null && !isMinor(v.multicolorCapMinor)) return null;
  return {
    mode: v.mode as DoodlesPricing["mode"],
    status: v.status as DoodlesPricing["status"],
    currency: v.currency,
    sizeBaseMinor: v.sizeBaseMinor,
    multicolorSurchargeMinor: v.multicolorSurchargeMinor as number | null,
    multicolorFloorMinor: v.multicolorFloorMinor,
    multicolorCapMinor: v.multicolorCapMinor as number | null,
    totalMinor: v.totalMinor,
    estimate: null
  };
}

export interface OrderSummary {
  orderId: string;
  partnerOrder?: true;
  /** Registry-derived storefront return. Optional during rolling deploys. */
  returnUrl?: string | null;
  item: {
    title: string;
    material: string | null;
    color: string | null;
    quantity: number;
    finish: string | null;
    /** Short-lived signed URL to a customer-safe picture of the item (the
     *  child's own drawing on a Doodles order). Optional during a rolling
     *  API/frontend deploy; null/absent renders the brand placeholder. */
    imageUrl?: string | null;
  };
  delivery: {
    serviceLevel: string;
    destinationCountryCode: string;
  };
  shippingAddress: ShippingAddress | null;
  // GROUPED consumer price, denominated in the CUSTOMER's currency (their pay-in
  // currency on a cross-currency order, else the provider/quote currency). The
  // customer never sees the provider's currency.
  price: {
    currency: string;
    itemSubtotalMinor: number;
    shippingMinor: number;
    totalMinor: number;
    // Present only when the locked price is a premium quote: the premium
    // fulfilment line, excluded from itemSubtotalMinor
    // (itemSubtotal + premium + shipping - discount = total).
    premiumServiceMinor?: number;
    // Present once a coupon is applied. totalMinor above is ALREADY the
    // discounted total (itemSubtotal + shipping - discount = total); these
    // two fields are only for rendering the applied-coupon row.
    couponCode?: string;
    discountMinor?: number;
  };
  disclosure: {
    soldBy: "tinkr";
    producedBy: "approved_local_partner";
    providerEntityType: "registered_business" | "individual" | null;
  };
  paymentStatus: "ready" | "paid";
  /** Doodles orders only; absent on every other order and during rolling deploys. */
  pricing?: DoodlesPricing | null;
}

export interface OrderPaidStatus {
  orderId: string;
  fulfillment?: {
    status: "preparing" | "in_production" | "shipped" | "delivered" | "completed" | "cancelled" | "under_review";
    carrier: string | null;
    trackingNumber: string | null;
    trackingUrl: string | null;
  };
  /** Registry-derived storefront return. Optional during rolling deploys. */
  returnUrl?: string | null;
  paid: boolean;
  /** Optional during a rolling API/frontend deploy; fall back to `paid`. */
  /** `cancelled`: the order was voided or refunded; never ask to pay again. */
  paymentStatus?: "pending" | "authorized" | "paid" | "cancelled";
  totalMinor: number | null;
  currency: string | null;
  orderReference: string;
}

export interface CheckoutSessionResponse {
  checkoutSessionId: string;
  url: string;
  orderId: string;
  amountMinor: number;
  currency: string;
  expiresAt: string;
  replayed: boolean;
}

export interface CustomerDocument {
  id: string;
  document_type: string;
  document_number: string | null;
  status: "issued" | "superseded";
  issued_at: string;
  currency: string | null;
  total_minor: string | null;
  tax_minor: string | null;
  live_mode: "live" | "test";
}

export interface CustomerDocumentsResponse {
  documents: CustomerDocument[];
  next_cursor: string | null;
}

export interface CustomerDocumentDownloadResponse {
  download_url: string;
  expires_in: number;
  filename: string;
}

export interface CouponApplyResponse {
  ok: true;
  couponCode: string;
  discountMinor: number;
  currency: string;
  totalAfterDiscountMinor: number;
}

export interface HandoffSummary {
  orderId: string;
  item: { title: string; material: string | null; color: string | null; quantity: number };
  delivery: { destinationCountryCode: string; serviceLevel: string };
  price: { currency: string; totalMinor: number };
  expiresAt: string;
  /** Wire value is untrusted until the handoff screen checks compatibility. */
  privacyNoticeVersion: string;
}

export interface VerificationResponse { verificationId: string; expiresAt: string }
export interface PrivateCheckoutResponse { checkoutUrl: string; orderId: string }
export interface OrderPrivacyResponse {
  orderId: string;
  source: string;
  email: string | null;
  shipping: unknown;
  locale: string | null;
  records: unknown[];
  retainedCategories: string[];
  erasure: { status: "none" | "pending" | "completed"; requestedAt?: string | null };
}
export interface ErasureResponse {
  ok: true;
  orderId: string;
  status: "completed" | "pending";
  retainedCategories: string[];
}

function postJson<T>(path: string, body: object): Promise<T> {
  return request<T>(path, { method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify(body) });
}

export function getHandoffSummary(token: string): Promise<HandoffSummary> {
  return request(`handoff/${encodeURIComponent(token)}/summary`);
}
export function startHandoffVerification(token: string, input: {
  email: string; locale: string; shipping: ShippingAddress; privacyNoticeVersion: PublicPrivacyNoticeVersion
}): Promise<VerificationResponse> {
  return postJson(`handoff/${encodeURIComponent(token)}/verification`, input);
}
export function verifyHandoff(token: string, verificationId: string, code: string): Promise<PrivateCheckoutResponse> {
  return postJson(`handoff/${encodeURIComponent(token)}/verify`, { verificationId, code });
}
export function getOrderPrivacy(token: string): Promise<OrderPrivacyResponse> {
  return request(`o/${encodeURIComponent(token)}/privacy`);
}
export function eraseOrderPrivacy(token: string): Promise<ErasureResponse> {
  return postJson(`o/${encodeURIComponent(token)}/privacy/erase`, {});
}
export function startOrderRecovery(token: string): Promise<VerificationResponse> {
  return postJson(`o/${encodeURIComponent(token)}/recovery`, {});
}
export function verifyOrderRecovery(token: string, verificationId: string, code: string): Promise<PrivateCheckoutResponse> {
  return postJson(`o/${encodeURIComponent(token)}/recovery/verify`, { verificationId, code });
}

/** Never navigate to a URL supplied by an API response without pinning its origin and route. */
export function privateCheckoutPath(rawUrl: string): string | null {
  try {
    const url = new URL(rawUrl);
    if (url.origin !== window.location.origin || !/^\/o\/[A-Za-z0-9_-]{16,128}$/.test(url.pathname)) return null;
    if (url.username || url.password || url.hash) return null;
    return url.pathname;
  } catch { return null; }
}

// --- Calls --------------------------------------------------------------------

export function getOrderSummary(token: string): Promise<OrderSummary> {
  return request<OrderSummary>(`o/${encodeURIComponent(token)}/summary`);
}

export function startCheckout(token: string): Promise<CheckoutSessionResponse> {
  return request<CheckoutSessionResponse>(`o/${encodeURIComponent(token)}/checkout-session`, {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: "{}"
  });
}

export function getOrderStatus(token: string, signal?: AbortSignal): Promise<OrderPaidStatus> {
  return request<OrderPaidStatus>(`o/${encodeURIComponent(token)}/status`, { signal });
}

export interface PaymentRefreshResponse {
  ok: true;
  orderId: string;
  refreshStatus: "queued" | "already_pending" | "already_confirmed" | "no_session";
  retryAfterSeconds: number;
}

/** Reconcile an existing server-selected payment; never creates another charge. */
export function refreshOrderPayment(token: string, signal?: AbortSignal): Promise<PaymentRefreshResponse> {
  return request(`o/${encodeURIComponent(token)}/payment-refresh`, {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: "{}",
    signal
  });
}

export function getOrderDocuments(token: string): Promise<CustomerDocumentsResponse> {
  return request<CustomerDocumentsResponse>(`o/${encodeURIComponent(token)}/documents`);
}

export function getOrderDocumentDownload(token: string, documentId: string): Promise<CustomerDocumentDownloadResponse> {
  return request<CustomerDocumentDownloadResponse>(`o/${encodeURIComponent(token)}/documents/${encodeURIComponent(documentId)}/download`);
}

export function applyCoupon(token: string, code: string): Promise<CouponApplyResponse> {
  return request<CouponApplyResponse>(`o/${encodeURIComponent(token)}/coupon`, {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify({ code })
  });
}

export function removeCoupon(token: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`o/${encodeURIComponent(token)}/coupon`, {
    method: "DELETE",
    headers: { "idempotency-key": crypto.randomUUID() }
  });
}

// --- Stub ---------------------------------------------------------------------

// Base (pre-discount) numbers for the stub order. Kept module-scope so both the
// summary GET and the coupon apply/remove handlers agree on the same order.
const STUB_ITEM_SUBTOTAL_MINOR = 3200;
const STUB_SHIPPING_MINOR = 500;
const STUB_CURRENCY = "JPY";

// The stub is stateful across calls within one page load so the UI (apply ->
// refetch summary -> remove -> refetch summary) is fully exercisable without a
// backend. "TINKR10" is the one code that succeeds (10% off the item
// subtotal); a handful of other fixed codes exercise each documented error
// path; anything else is treated as not found.
let stubAppliedCoupon: { code: string; discountMinor: number } | null = null;

const STUB_COUPON_ERRORS: Record<string, string> = {
  EXPIREDCODE: "coupon_expired",
  REVOKEDCODE: "coupon_revoked",
  NOTSTARTEDCODE: "coupon_not_started",
  CHANNELCODE: "coupon_not_applicable_channel",
  CURRENCYCODE: "coupon_currency_mismatch",
  LIMITCODE: "coupon_usage_limit_reached",
  TOTALCODE: "coupon_not_applicable_to_total",
  UNSUPPORTEDCODE: "coupon_not_supported_for_order",
  SESSIONCODE: "checkout_session_active"
};

function stubSummary(): OrderSummary {
  const discountMinor = stubAppliedCoupon?.discountMinor ?? 0;
  const totalMinor = STUB_ITEM_SUBTOTAL_MINOR + STUB_SHIPPING_MINOR - discountMinor;
  return {
    orderId: "00000000-0000-4000-8000-00000000stub",
    returnUrl: "https://fourth-app.example.test/o/stub-order?lang=en",
    item: {
      title: "Custom Desk Organizer",
      material: "PLA",
      color: "Matte Black",
      quantity: 1,
      finish: null,
      // Inline stand-in for the signed item picture so the stub exercises the
      // image tile (a live Doodles order signs the child's drawing here).
      imageUrl:
        "data:image/svg+xml," +
        encodeURIComponent(
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">' +
            '<rect width="72" height="72" fill="#fff"/>' +
            '<path d="M20 52 L36 20 L52 52 Z" fill="none" stroke="#e8590c" stroke-width="4" stroke-linejoin="round"/>' +
            '<circle cx="36" cy="44" r="5" fill="#0b7285"/>' +
            "</svg>"
        )
    },
    delivery: { serviceLevel: "tracked", destinationCountryCode: "JP" },
    shippingAddress: {
      countryCode: "JP",
      postalCode: "150-0001",
      prefecture: "Tokyo",
      city: "Shibuya",
      town: "Jingumae 1-2-3",
      building: "Shibuya Building 401",
      recipientName: "山田 太郎",
      recipientNameKana: "ヤマダ タロウ",
      phone: "03-1234-5678"
    },
    price: {
      currency: STUB_CURRENCY,
      itemSubtotalMinor: STUB_ITEM_SUBTOTAL_MINOR,
      shippingMinor: STUB_SHIPPING_MINOR,
      totalMinor,
      ...(stubAppliedCoupon
        ? { couponCode: stubAppliedCoupon.code, discountMinor: stubAppliedCoupon.discountMinor }
        : {})
    },
    disclosure: {
      soldBy: "tinkr",
      producedBy: "approved_local_partner",
      providerEntityType: "individual"
    },
    paymentStatus: "ready"
  };
}

function stubMulticolorPricing(status: DoodlesPricing["status"], surchargeMinor: number | null): DoodlesPricing {
  const sizeBaseMinor = 11800;
  const floor = 4100;
  return {
    mode: "slice", status, currency: "MYR", sizeBaseMinor, multicolorSurchargeMinor: surchargeMinor,
    multicolorFloorMinor: floor, multicolorCapMinor: 9000,
    totalMinor: sizeBaseMinor + (surchargeMinor ?? floor),
    estimate: status === "final" ? { filamentG: 212, printSeconds: 41_400 } : null
  };
}

function stubRequest<T>(path: string, method: string, body?: string): Promise<T> {
  if (method === "GET" && path === "o/partnerreviewstub/status") {
    const status: OrderPaidStatus = {
      orderId: "00000000-0000-4000-8000-00000000stub",
      returnUrl: null, paid: true, paymentStatus: "paid", totalMinor: 3700,
      currency: "JPY", orderReference: "TK-REVIEW01",
      fulfillment: { status: "under_review", carrier: null, trackingNumber: null, trackingUrl: null }
    };
    return Promise.resolve(status as T);
  }
  if (method === "GET" && path === "o/partnerreviewstub/documents") {
    return Promise.resolve({ documents: [], next_cursor: null } as T);
  }
  // Local preview fixture for the durable partner customer page. It is only
  // compiled when VITE_E2E_API_STUB=true and never represents a live order.
  if (method === "GET" && path === "o/partnercheckoutstub/status") {
    return Promise.resolve({
      orderId: "00000000-0000-4000-8000-00000000stub",
      returnUrl: null, paid: true, paymentStatus: "paid", totalMinor: 3700,
      currency: "JPY", orderReference: "TK-DEMO1234",
      fulfillment: { status: "shipped", carrier: "Parcel Co", trackingNumber: "DEMO-123456",
        trackingUrl: "https://carrier.example/track/DEMO-123456" }
    } as T);
  }
  if (method === "GET" && path === "o/partnercheckoutstub/documents") {
    return Promise.resolve({ documents: [{
      id: "00000000-0000-4000-8000-0000000000f1", document_type: "customer_receipt",
      document_number: "RC-DEMO1234", status: "issued", issued_at: "2026-09-24T00:00:00Z",
      currency: "JPY", total_minor: "3700", tax_minor: "0", live_mode: "test"
    }], next_cursor: null } as T);
  }
  if (method === "GET" && path === "o/partnercheckoutstub/documents/00000000-0000-4000-8000-0000000000f1/download") {
    return Promise.resolve({ download_url: "https://example.com/receipt.pdf", expires_in: 120,
      filename: "demo-receipt.pdf" } as T);
  }
  // Doodles slice-priced multicolor fixtures (ADR-DSP1) for the local preview.
  if (method === "GET" && path === "o/multicolor-pending-stub/summary") {
    return Promise.reject(new CheckoutApiError(409, DOODLES_PRICE_PENDING, "Price pending", null,
      { pricing: stubMulticolorPricing("pending_estimate", null) }));
  }
  if (method === "GET" && (path === "o/multicolor-final-stub/summary" || path === "o/multicolor-cap-stub/summary")) {
    const cap = path === "o/multicolor-cap-stub/summary";
    const surcharge = cap ? 9000 : 5600;
    const pricing = stubMulticolorPricing(cap ? "fallback_cap" : "final", surcharge);
    const base = stubSummary();
    return Promise.resolve({
      ...base,
      item: { ...base.item, title: "Doodle figurine (M, multicolor)", material: "PLA", color: null },
      delivery: { serviceLevel: "tracked", destinationCountryCode: "MY" },
      shippingAddress: { countryCode: "MY", recipientName: "Aisyah Rahman", line1: "12 Jalan Ampang",
        line2: "", city: "Kuala Lumpur", state: "WP Kuala Lumpur", postalCode: "50450", phone: "" },
      price: { currency: "MYR", itemSubtotalMinor: pricing.totalMinor, shippingMinor: 0, totalMinor: pricing.totalMinor },
      pricing
    } as T);
  }
  if (method === "GET" && /^o\/[^/]+\/summary$/.test(path)) {
    return Promise.resolve(stubSummary() as T);
  }
  if (method === "GET" && /^o\/[^/]+\/status$/.test(path)) {
    const pending = path === "o/pendingcheckoutstub/status";
    const status: OrderPaidStatus = {
      orderId: "00000000-0000-4000-8000-00000000stub",
      returnUrl: "https://fourth-app.example.test/o/stub-order?lang=en",
      paid: !pending,
      paymentStatus: pending ? "pending" : "paid",
      totalMinor: 3700,
      currency: STUB_CURRENCY,
      orderReference: "TK-STUB0001"
    };
    return Promise.resolve(status as T);
  }
  if (method === "POST" && /^o\/[^/]+\/payment-refresh$/.test(path)) {
    return Promise.resolve({ ok: true, orderId: "00000000-0000-4000-8000-00000000stub",
      refreshStatus: "queued", retryAfterSeconds: 15 } as T);
  }
  if (method === "POST" && /^o\/[^/]+\/checkout-session$/.test(path)) {
    const session: CheckoutSessionResponse = {
      checkoutSessionId: "cs_test_stub",
      url: "about:blank",
      orderId: "00000000-0000-4000-8000-00000000stub",
      amountMinor: 3700,
      currency: STUB_CURRENCY,
      expiresAt: "2099-01-01T00:00:00.000Z",
      replayed: false
    };
    return Promise.resolve(session as T);
  }
  if (method === "POST" && /^o\/[^/]+\/coupon$/.test(path)) {
    let code = "";
    try {
      code = typeof JSON.parse(body ?? "{}").code === "string" ? JSON.parse(body ?? "{}").code : "";
    } catch {
      code = "";
    }
    const normalized = code.trim().toUpperCase();

    if (stubAppliedCoupon) {
      return Promise.reject(
        new CheckoutApiError(409, "coupon_already_applied", "A coupon is already applied to this order")
      );
    }
    if (normalized === "TINKR10") {
      const discountMinor = Math.round(STUB_ITEM_SUBTOTAL_MINOR * 0.1);
      stubAppliedCoupon = { code: normalized, discountMinor };
      const response: CouponApplyResponse = {
        ok: true,
        couponCode: normalized,
        discountMinor,
        currency: STUB_CURRENCY,
        totalAfterDiscountMinor: STUB_ITEM_SUBTOTAL_MINOR + STUB_SHIPPING_MINOR - discountMinor
      };
      return Promise.resolve(response as T);
    }
    const knownErrorCode = STUB_COUPON_ERRORS[normalized];
    if (knownErrorCode) {
      return Promise.reject(new CheckoutApiError(409, knownErrorCode, `Stub coupon error: ${knownErrorCode}`));
    }
    return Promise.reject(new CheckoutApiError(404, "coupon_not_found", "Coupon code not found"));
  }
  if (method === "DELETE" && /^o\/[^/]+\/coupon$/.test(path)) {
    if (!stubAppliedCoupon) {
      return Promise.reject(new CheckoutApiError(409, "no_coupon_applied", "No coupon is applied to this order"));
    }
    stubAppliedCoupon = null;
    return Promise.resolve({ ok: true } as T);
  }
  return Promise.reject(new CheckoutApiError(503, "stubbed", "Not available in the stub"));
}
